/**
 * Modules Page - Global module management
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useModules, useUpdateModule } from '../hooks';
import { useSnackbar } from 'notistack';
import type { Module } from '../types';

export function ModulesPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { data: modules, isLoading } = useModules();
  const updateModule = useUpdateModule();

  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [editForm, setEditForm] = useState({
    basePrice: 0,
    isActive: true,
  });

  const handleEditClick = (module: Module) => {
    setEditingModule(module);
    setEditForm({
      basePrice: module.basePrice,
      isActive: module.isActive,
    });
  };

  const handleSave = async () => {
    if (!editingModule) return;

    try {
      await updateModule.mutateAsync({
        moduleKey: editingModule.key,
        dto: editForm,
      });
      enqueueSnackbar(`${editingModule.name} oppdatert`, { variant: 'success' });
      setEditingModule(null);
    } catch {
      enqueueSnackbar('Kunne ikke oppdatere modul', { variant: 'error' });
    }
  };

  // Get price display text
  const getPriceText = (module: Module): string => {
    if (module.isCore) {
      return 'Grunnpakke';
    }
    if (module.basePrice > 0) {
      return `${module.basePrice} kr/mnd`;
    }
    return 'Gratis';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Moduler
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administrer moduler og priser
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Her kan du sette <strong>standardpriser</strong> for alle moduler. Prisene gjelder for nye abonnementer.
        For å sette <strong>individuelle priser</strong> per tenant, gå til tenant-detaljsiden.
      </Alert>

      {isLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <Skeleton height={200} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {modules?.map((module) => (
            <Grid key={module.key} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                sx={{
                  height: '100%',
                  opacity: module.isActive ? 1 : 0.6,
                  position: 'relative',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {module.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {module.isCore && (
                        <Chip label="Grunnpakke" size="small" color="primary" />
                      )}
                      {!module.isActive && (
                        <Chip label="Deaktivert" size="small" color="default" />
                      )}
                      {!module.isCore && (
                        <Tooltip title="Rediger modul">
                          <IconButton size="small" onClick={() => handleEditClick(module)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                    {module.description}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {module.key}
                    </Typography>
                    <Typography
                      variant="h6"
                      color={module.isCore ? 'primary' : module.basePrice > 0 ? 'text.primary' : 'success.main'}
                      fontWeight={600}
                    >
                      {getPriceText(module)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Module Dialog */}
      <Dialog open={!!editingModule} onClose={() => setEditingModule(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            Rediger {editingModule?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label="Standardpris"
              type="number"
              value={editForm.basePrice}
              onChange={(e) => setEditForm({ ...editForm, basePrice: parseFloat(e.target.value) || 0 })}
              InputProps={{
                endAdornment: <InputAdornment position="end">kr/mnd</InputAdornment>,
              }}
              helperText="Sett til 0 for gratis modul"
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                />
              }
              label={editForm.isActive ? 'Tilgjengelig for tenants' : 'Kommer snart (ikke tilgjengelig)'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingModule(null)}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updateModule.isPending}
          >
            Lagre
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
